import {
  Copy,
  CreditCard,
  Eye,
  Search,
  Trash2,
  X,
  CheckCircle,
  RotateCcw,
} from "lucide-react";
import { useState } from "react";
import { toast } from "react-toastify";
import LoadingScreen from "../../../utils/LoadingScreen";
import { formatDateTime } from "../../../utils/dateFormatter";

const decodeEmoji = (text = "") => {
  return String(text).replace(/\[Emoji:([^\]]+)\]/g, (_, code) => {
    try {
      const bin = atob(code);

      return decodeURIComponent(
        bin
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );
    } catch {
      return "";
    }
  });
};

const InvoiceTable = ({
  rows = [],
  loading,
  onDeleteClick,
  onPaymentClick,
  onRefresh,
}) => {
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentMode, setPaymentMode] = useState("UPI");
  const [isPaid, setIsPaid] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");

  const handleSubmit = () => {
    onPaymentClick(selectedInvoice, {
      paymentMode,
      paymentStatus: isPaid ? "paid" : "unpaid",
    });

    setPaymentOpen(false);
  };

  const handleDeleteConfirm = () => {
    onDeleteClick(selectedId);

    setDeleteOpen(false);
    setSelectedId(null);
  };

  const filteredRows = rows.filter((r) => {
    const q = search.toLowerCase();

    return (
      r.invoice_number?.toLowerCase().includes(q) ||
      decodeEmoji(r.customer_name)?.toLowerCase().includes(q) ||
      r.customer_mobile?.toLowerCase().includes(q)
    );
  });

  return (
    <>
      {/* HEADER */}
  
      <div className="flex justify-between items-center mb-4 montserrat-font-1">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            Order Invoices
          </h2>
          <p className="text-sm text-gray-500 montserrat-font-2">
            View, download, update payment status, and manage invoices
          </p>
        </div>

        <div className="flex gap-3">
          <div className="flex items-center border rounded-lg px-3 py-2 bg-white w-72">
            <Search size={14} className="text-gray-400 mr-2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="outline-none text-sm w-full"
              placeholder="Search by invoice no, name"
            />
          </div>

    <button
  onClick={onRefresh}
  className="border px-3 py-2 rounded-lg text-white text-sm bg-green-500 flex items-center gap-2"
>
    <RotateCcw className="w-4 h-4" />
   Refresh
</button>
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-xl border shadow-sm overflow-auto h-full montserrat-font-1">
        <table className="w-full text-sm montserrat-font-1">
          <thead className="sticky top-0 z-10 bg-green-100 text-xs uppercase">
            <tr>
              <th className="p-4 text-left">Invoice #</th>
              <th className="p-4 text-left">Customer</th>
              <th className="p-4 text-left">Mobile</th>
                <th className="p-4 text-left">SubTotal</th>
              <th className="p-4 text-left">Amount</th>
              <th className="p-4 text-left">Status</th>
              <th className="p-4 text-left">Created On</th>
              <th className="p-4 text-left">Payment</th>
              <th className="p-4 text-left">Actions</th>
            </tr>
          </thead>

          <tbody>
           {loading ? (
  <tr>
    <td colSpan="8" className="py-10">
      <div className="flex justify-center">
         <LoadingScreen text="Loading..." />
      </div>
    </td>
  </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center py-6 text-gray-400">
                  No invoices found
                </td>
              </tr>
            ) : (
              filteredRows.map((r) => (
                <tr key={r.pk_invoice_id} className="border-t hover:bg-gray-50">
                  <td className="p-4 text-blue-600 font-medium">
                    {r.invoice_number}
                  </td>

                  <td>{decodeEmoji(r.customer_name)}</td>

                  <td>{r.customer_mobile}</td>

      <td className="font-mono">
  ₹ {Number(r.sub_total || 0).toFixed(2)}
</td>

                  <td className="font-mono">
                    ₹ {Number(r.amount || 0).toFixed(2)}
                  </td>

                  <td>
  <span
    className={`inline-block text-center w-20 px-3 py-1 rounded-full text-xs
      ${
        r.status === "paid"
          ? "bg-green-100 text-green-700"
          : "bg-red-200 text-red-600"
      }`}
  >
    {r.status?.toUpperCase()}
  </span>
</td>

                  <td className="text-xs text-gray-500">
  {formatDateTime(r.created_on, true)}
</td>

                  <td>
                    <button
                      onClick={() => {
                        setSelectedInvoice(r);
                        setPaymentOpen(true);
                      }}
                      className="flex items-center gap-2 bg-green-500 text-white px-3 py-1 rounded-full text-xs"
                    >
                      <CreditCard size={14} />
                      <span>Payment</span>
                    </button>
                  </td>

                  <td>
                    <div className="flex gap-2">
                      {/* VIEW */}
                      <button
                        onClick={() => {
                          const imageUrl = `${r.invoice_link}`;
                          const html = `
      <html>
        <head>
          <title>Invoice</title>
          <style>
            body { margin: 0; background: #f3f4f6; display: flex; justify-content: center; padding: 20px; }
            img { max-width: 100%; height: auto; box-shadow: 0 4px 20px rgba(0,0,0,0.15); border-radius: 8px; }
          </style>
        </head>
        <body>
          <img src="${imageUrl}" alt="Invoice" />
        </body>
      </html>
    `;
                          const newTab = window.open("", "_blank");
                          newTab.document.write(html);
                          newTab.document.close();
                        }}
                        className="flex items-center gap-2 px-3 py-1 border rounded-full text-xs"
                      >
                        <Eye size={14} />
                        <span>View</span>
                      </button>

                      {/* COPY */}
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(r.invoice_link);
                          toast.success("Copied!");
                        }}
                        className="flex items-center gap-2 bg-blue-500 text-white px-3 py-1 rounded-full text-xs"
                      >
                        <Copy size={14} />
                        <span>Copy</span>
                      </button>

                      {/* DELETE */}
                      <button
                        onClick={() => {
                          setSelectedId(r.pk_invoice_id);
                          setDeleteOpen(true);
                        }}
                        className="flex items-center gap-2 border border-red-300 text-red-500 px-3 py-1 rounded-full text-xs"
                      >
                        <Trash2 size={14} />
                        <span>Delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* payment model  */}

        {paymentOpen && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 montserrat-font-1">
            <div className="bg-white w-[420px] rounded-2xl shadow-xl overflow-hidden montserrat-font-1">
              {/* HEADER */}
              <div className="flex justify-between items-center px-5 py-4 border-b bg-gray-50">
                <div className="flex items-center gap-2">
                  <CreditCard size={18} className="text-gray-600" />
                  <h3 className="font-semibold text-gray-800">
                    Update Payment
                  </h3>
                </div>

                <button
                  onClick={() => setPaymentOpen(false)}
                  className="p-1 rounded-full hover:bg-gray-200"
                >
                  <X size={18} />
                </button>
              </div>

              {/* BODY */}
              <div className="p-5 space-y-5">
                {/* INVOICE */}
                <div>
                  <label className="text-sm text-gray-600">Invoice</label>
                  <div className="mt-1 border rounded-xl px-3 py-2 bg-gray-50 flex justify-between items-center">
                    <span className="text-sm font-medium">
                      {selectedInvoice?.invoice_number}
                    </span>
                    <span className="font-mono text-sm text-gray-700">
                      ₹ {Number(selectedInvoice?.amount || 0).toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* PAYMENT MODE */}
                <div>
                  <label className="text-sm text-gray-600">Payment Mode</label>

                  <div className="mt-1 relative">
                    <CreditCard
                      size={16}
                      className="absolute left-3 top-3 text-gray-400"
                    />

                    <select
                      value={paymentMode}
                      onChange={(e) => setPaymentMode(e.target.value)}
                      className="w-full border rounded-xl pl-9 pr-3 py-2 text-sm bg-white"
                    >
                      <option value="UPI">UPI</option>
                      <option value="CASH">CASH</option>
                    </select>
                  </div>

                  <p className="text-xs text-gray-400 mt-1">
                    Default is UPI. Change to CASH if paid in cash.
                  </p>
                </div>

                {/* STATUS */}
                <div>
                  <label className="text-sm text-gray-600">
                    Payment Status
                  </label>

                  <div
                    onClick={() => setIsPaid(!isPaid)}
                    className="mt-2 border rounded-full px-4 py-2 flex items-center gap-2 cursor-pointer bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={isPaid}
                      onChange={() => setIsPaid(!isPaid)}
                      className="accent-green-500 cursor-pointer"
                    />
                    <span className="text-sm">Mark as Paid</span>
                  </div>
                </div>
              </div>

              {/* FOOTER */}
              <div className="flex justify-between items-center px-5 py-4 border-t bg-gray-50">
                <button
                  onClick={() => setPaymentOpen(false)}
                  className="px-4 py-2 rounded-full border text-sm bg-white hover:bg-gray-100"
                >
                  Cancel
                </button>

                <button
                  onClick={handleSubmit}
                  className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-full text-sm shadow"
                >
                  <CheckCircle size={16} />
                  Update Payment
                </button>
              </div>
            </div>
          </div>
        )}

        {/* delete model */}

        {deleteOpen && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 montserrat-font-1">
            <div className="bg-white w-[360px] rounded-xl shadow-lg p-6 montserrat-font-1">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                
                Delete Invoice
              </h3>

              <p className="text-sm text-gray-500 mb-5">
                Are you sure you want to delete this invoice?
              </p>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setDeleteOpen(false);
                    setSelectedId(null);
                  }}
                  className="px-4 py-2 rounded-lg border bg-gray-100"
                >
                  Cancel
                </button>

                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 rounded-lg bg-red-500 text-white"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default InvoiceTable;
